import { reactive } from 'vue'
import axios from 'axios'
import dayjs from 'dayjs'
import LoginStore from '@/stores/CommonStores/LoginStore/LoginStore'
import APIAccessor from '@/util/APIAccesser'
import { ContractData, ContractMaster } from '@/stores/registerTeam/EnterAccountStoreTypes'
import CourseMaster from '@/stores/registerTeam/CourseMaster'
import { StoreBase } from '../StoreBase'

/**
 * トライアルプランの契約情報の終了日時を返す
 * @param organizationType 組織種別
 */
const endDate = (): number => {
  // 開始日から1ヶ月間をトライアルの期間にする
  const trialPeriod = 1
  return dayjs()
    .add(trialPeriod, 'month')
    .subtract(1, 'day')
    .endOf('day')
    .valueOf()
}

class EnterAccountStore implements StoreBase {
  /* eslint-disable class-methods-use-this */
  createStore() {
    const state = reactive({
      tokenId: '',
      displayName: '',
      username: '',
      password: '',
      telNumber: '',
      countryIso2: '',
      isInvitedUser: false,
      // FLUXBACK-2532 の恒久対応まで LINE 登録を停止しているため、設定されない
      lineUserId: '',
      isExistingAccount: false,
      accessToken: '',
      organizationName: '',
      organizationType: '',
      courseId: '',
      // 新規組織作成時にですでに所属済み組織があるかどうか
      hasAffiliatedOrganizations: false,
      isOfflinePlan: false,
    })

    /**
     * ストアに格納されている登録トークンから、APIに有効性を確認する。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1135870048/07+API
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1135870035/08+API
     * @return 送信結果。
     */
    const validateToken = () => {
      const postfixUrl = state.isInvitedUser ? 'invite_token' : 'regist_token'
      return axios
        .get(`${process.env.VUE_APP_API_BASE_URL}/manage/${postfixUrl}/${state.tokenId}/validate`)
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          console.error('error', error)
          return { isSuccess: false, res: error }
        })
    }

    /**
     * ストアに格納されている値からユーザーを登録する。
     * 新規ユーザーの場合と招待ユーザーの場合で実行するAPIは違うことに注意
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1144094729/API
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1144127604/API
     * @return 送信結果。
     */
    const registerUser = async () => {
      const suffixUrl = state.isInvitedUser ? 'invited_user' : 'initial_user'
      const data = state.isExistingAccount
        ? {
            tokenId: state.tokenId,
            accessToken: state.isExistingAccount ? state.accessToken : null,
          }
        : {
            tokenId: state.tokenId,
            displayName: state.displayName,
            username: state.hasAffiliatedOrganizations ? undefined : state.username,
            password: state.hasAffiliatedOrganizations ? undefined : state.password,
            telNumber: state.telNumber,
            snsAccountInfo: state.lineUserId
              ? {
                  lineUserId: state.lineUserId,
                }
              : null,
          }
      const serviceRegistrationRes = await axios
        .post(
          `${process.env.VUE_APP_API_BASE_URL}/manage/service_registration/${suffixUrl}`,
          data,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes }
        })
      return serviceRegistrationRes
    }
    /**
     * 契約情報を作成する
     * @param mail 登録するメールアドレス
     * @param lineUserId 登録するラインのID
     */
    const createContract = async (mail: string | null, lineUserId: string | null) => {
      try {
        // 現在利用している契約情報を取得する
        const contractData = await APIAccessor.request<ContractData[]>({
          path: 'manage/contract_info',
          method: 'GET',
        })

        const { contractInfoId } = contractData.data[0]

        // 契約情報作成時に必要なデータを追加する
        const updateContractData: Partial<ContractData> = {
          ...contractData.data[0],
          currency: process.env.VUE_APP_CURRENCY,
          customerInfo: {
            address: null,
            corporateName: null,
            country: state.isInvitedUser ? null : state.countryIso2,
            mail,
            name: LoginStore.name,
            tel: state.isInvitedUser ? null : state.telNumber,
            snsAccountInfo: lineUserId
              ? {
                  lineUserId,
                  lineAuthorizationCode: null,
                }
              : null,
          },
          payment: { method: 'BANK_TRANSFER', yearlyMethod: 'BANK_TRANSFER', paymentService: null },
          taxRegion: process.env.VUE_APP_TAX_REGION,
        }

        // 契約情報を作成する
        await APIAccessor.request({
          path: `manage/contract_info/${contractInfoId}`,
          method: 'PUT',
          data: updateContractData,
        })

        // プランを作成する
        const contractMaster = await APIAccessor.request<ContractMaster[]>({
          path: 'data/master/contract_plan',
          method: 'GET',
        })

        let freePlanGroupId = 'freePlan'
        if (state.organizationType === 'MBP') {
          freePlanGroupId = 'mbpPlan'
        } else if (state.isOfflinePlan) {
          freePlanGroupId = 'freeOfflinePlan'
        }
        const readOnlyPlanGroupId =
          state.organizationType === 'MBP' ? 'mbpReadonlyPlan' : 'freeReadonlyPlan'

        const freeContractIds = contractMaster.data
          .filter(contract => contract.planGroupId === freePlanGroupId)
          .map(contract => contract.contractPlanId)

        const readOnlyPlanIds = contractMaster.data
          .filter(contract => contract.planGroupId === readOnlyPlanGroupId)
          .map(contract => contract.contractPlanId)

        let freePlanEndDate = endDate()
        // オフラインフリープランの終了日を2026-9-30にするための暫定対応
        if (state.isOfflinePlan)
          freePlanEndDate = dayjs('2026-9-30')
            .endOf('day')
            .valueOf()
        let readonlyEndDate = dayjs(freePlanEndDate)
          .add(3, 'month')
          .valueOf()
        // 組織タイプがMBPで、かつコースが選択されている場合
        if (state.organizationType === 'MBP' && state.courseId) {
          const courseApiResult = await APIAccessor.request<CourseMaster>({
            path: `data/master/course/${state.courseId}`,
            method: 'GET',
          })
          // planDuration / readOnlyDuration が設定されている場合
          if (courseApiResult.data?.planDuration && courseApiResult.data?.readOnlyDuration) {
            freePlanEndDate = dayjs().valueOf() + courseApiResult.data.planDuration
            readonlyEndDate = freePlanEndDate + 1 + courseApiResult.data.readOnlyDuration
          }
          // planEndDate / readOnlyEndDate が設定されている場合
          if (courseApiResult.data?.planEndDate && courseApiResult.data?.readOnlyEndDate) {
            freePlanEndDate = courseApiResult.data.planEndDate
            readonlyEndDate = courseApiResult.data.readOnlyEndDate
          }
        }
        const plans = [
          ...freeContractIds.map(id => {
            return {
              contractPlanId: id as string | null,
              startDate: new Date().getTime(),
              endDate: freePlanEndDate,
              renewalMonth: null,
            }
          }),
        ]

        if (!state.isOfflinePlan) {
          plans.push(
            ...readOnlyPlanIds.map(id => {
              return {
                contractPlanId: id as string | null,
                startDate: freePlanEndDate + 1,
                endDate: readonlyEndDate,
                renewalMonth: null,
              }
            }),
          )
        }

        // 契約情報契約プランを作成する
        const result = await APIAccessor.request({
          path: `manage/contract_info/${contractInfoId}/plans`,
          method: 'POST',
          data: {
            plans,
          },
        })

        return {
          isSuccess: true,
          res: result,
        }
      } catch (e) {
        console.warn(e)
        // ネットワークエラー対策
        if (e.response) {
          return { isSuccess: false, res: e }
        }
        // eslint-disable-next-line @typescript-eslint/camelcase
        return { isSuccess: false, res: { response: { data: { error_code: 'NETWORK_ERROR' } } } }
      }
    }
    /**
     * ユーザー登録 → 契約情報作成までの一連の流れを実行する
     */
    const submit = async () => {
      const createUserResult = await registerUser()
      if (!createUserResult.isSuccess) {
        return createUserResult
      }
      // 初期ユーザーの場合は契約情報を作成する
      if (!state.isInvitedUser) {
        // ログイン情報を設定する
        if (createUserResult.res?.data?.authenticationResponse)
          LoginStore.setLoginState(createUserResult.res.data.authenticationResponse)
        return createContract(
          createUserResult.res.data.manageUser.mailAddress,
          createUserResult.res.data.manageUser.snsAccountInfo?.lineUserId,
        )
      }
      return createUserResult
    }

    /**
     * 入力されているユーザIDが重複しているものかAPIへ値を送信し結果を得る。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1553989841/API
     * @return 検査結果。
     */
    const checkDuplicateUserId = async () => {
      const suffixUrl = state.isInvitedUser ? 'invite_token' : 'regist_token'
      const result = await axios
        .get(
          `${process.env.VUE_APP_API_BASE_URL}/manage/service_registration/${suffixUrl}/${state.tokenId}/validate/username/${state.username}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes }
        })
      return result
    }
    /**
     * ログインを実行し、アクセストークンをストアに保存する。
     */
    const fetchAccessToken = async () => {
      const res = await LoginStore.login(state.username, state.password)
      state.accessToken = LoginStore.accessToken
      return res
    }

    return {
      submit,
      validateToken,
      checkDuplicateUserId,
      fetchAccessToken,
      /**
       * 登録トークンID
       */
      get tokenId(): string {
        return state.tokenId
      },
      /**
       * 登録トークンID
       * @param tokenId 登録トークンID
       */
      set tokenId(tokenId: string) {
        state.tokenId = tokenId
      },
      /**
       * ログインユーザ名
       */
      get username() {
        return state.username
      },
      /**
       * ログインユーザ名
       * @param username ログインユーザ名
       */
      set username(username: string) {
        state.username = username
      },
      /**
       * 表示名
       */
      get displayName() {
        return state.displayName
      },
      /**
       * 表示名
       * @param displayName 表示名
       */
      set displayName(displayName: string) {
        state.displayName = displayName
      },
      /**
       * パスワード
       */
      get password() {
        return state.password
      },
      /**
       * パスワード
       * @param password パスワード
       */
      set password(password: string) {
        state.password = password
      },
      /**
       * 電話番号
       */
      get telNumber() {
        return state.telNumber
      },
      /**
       * 電話番号
       * @param telNumber 電話番号
       */
      set telNumber(telNumber: string) {
        state.telNumber = telNumber
      },
      /**
       * 居住地
       */
      get countryIso2() {
        return state.countryIso2
      },
      /**
       * 居住地
       * @param countryIso2 居住地
       */
      set countryIso2(countryIso2: string) {
        state.countryIso2 = countryIso2
      },
      /**
       * 招待ユーザ判別フラグ
       * 招待ユーザーの場合trueを返す
       */
      get isInvitedUser() {
        return state.isInvitedUser
      },
      /**
       * 招待ユーザ判別フラグ
       * @param isInvitedUser 招待ユーザ判別フラグ
       */
      set isInvitedUser(isInvitedUser: boolean) {
        state.isInvitedUser = isInvitedUser
      },
      /**
       * 既存のアカウント判別フラグ
       * 既存のアカウントに組織を追加する場合trueを返す
       */
      get isExistingAccount() {
        return state.isExistingAccount
      },
      /**
       * 既存のアカウント判別フラグ
       * @param isExistingAccount 既存のアカウント判別フラグ
       */
      set isExistingAccount(isExistingAccount: boolean) {
        state.isExistingAccount = isExistingAccount
      },
      /**
       * アクセストークンを取得する。
       */
      get accessToken() {
        return state.accessToken
      },
      /**
       * 組織名
       */
      get organizationName(): string {
        return state.organizationName
      },
      /**
       * 組織名
       * @param organizationName 組織名
       */
      set organizationName(organizationName: string) {
        state.organizationName = organizationName
      },
      /**
       * 組織種別
       */
      get organizationType(): string {
        return state.organizationType
      },
      /**
       * 組織種別
       * @param organizationType 組織種別
       */
      set organizationType(organizationType: string) {
        state.organizationType = organizationType
      },
      /**
       * LINE登録かどうかの判別フラグ
       * LINE登録の際にTrueを返す
       */
      get isLine(): boolean {
        return !!state.lineUserId
      },
      /**
       * コースIDを設定する。
       * @param courseId コースID
       */
      set courseId(courseId: string) {
        state.courseId = courseId
      },
      /**
       * コースIDを取得する。
       */
      get courseId(): string {
        return state.courseId
      },
      /**
       * 組織に所属済みのユーザかどうかを設定する。
       * @param courseId コースID
       */
      set hasAffiliatedOrganizations(hasAffiliatedOrganizations: boolean) {
        state.hasAffiliatedOrganizations = hasAffiliatedOrganizations
      },
      /**
       * 組織に所属済みのユーザかどうかを取得する。
       */
      get hasAffiliatedOrganizations(): boolean {
        return state.hasAffiliatedOrganizations
      },
      /**
       * MBP組織かどうか
       */
      get isMbpOrg(): boolean {
        return state.organizationType === 'MBP'
      },
      /**
       * 登録するプランがオフラインフリープランかどうかを設定する
       * @param isOfflinePlan 登録するプランがオフラインフリープランかの判別フラグ
       */
      set isOfflinePlan(isOfflinePlan: boolean) {
        state.isOfflinePlan = isOfflinePlan
      },
      /**
       * 登録するプランがオフラインフリープランかどうかを取得する
       */
      get isOfflinePlan() {
        return state.isOfflinePlan
      },
    }
  }

  /* eslint-enable class-methods-use-this */
}

export default new EnterAccountStore().createStore()
